<template>
  <div class="sidebar-option__wrapper" :class="{ isActive: selected }">
    <div class="sidebar-option__tip" :class="{ hidden: !selected }"></div>
    <div class="sidebar-icon" :class="{ 'text-logo': selected }">
      <div
        v-if="isImpersonated && name === 'Settings'"
        class="flex flex-col justify-center items-center gap-[7px]"
      >
        <el-tooltip
          :content="toolTipContent"
          :raw-content="true"
          placement="right"
          popper-class="applied-filters-tooltip"
        >
          <div
            class="h-7 w-7 rounded-full text-center !bg-orange-100 flex justify-center items-center"
          >
            <span class="text-white font-normal">{{ userFirstInitial }}</span>
          </div>
        </el-tooltip>
      </div>
      <div v-else class="flex flex-col items-center justify-center gap-[7px]">
        <ClientOnly v-if="toolTipText !== ''">
          <el-tooltip
            :content="toolTipText"
            :raw-content="true"
            placement="right"
            popper-class="sidebar-tooltip"
          >
            <font-awesome-icon
              class="text-lg focus:outline-none"
              :icon="icon"
            />
          </el-tooltip>
        </ClientOnly>

        <ClientOnly v-else>
          <font-awesome-icon class="text-lg focus:outline-none" :icon="icon" />
        </ClientOnly>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useUserStore } from "@/store/user";
import { useOrgStore } from "@/store/org";
import { replaceAsterisks } from "~/utils/replaceAsterisks";

const userStore = useUserStore();
const user = process.client ? userStore.getUser : null;
const orgStore = useOrgStore();
const org = process.client ? orgStore.getOrg : null;

const { t } = useI18n();

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  toolTipText: {
    type: String,
    required: false,
    default: "",
  },
  displayText: {
    type: String,
    required: true,
  },
  icon: {
    type: [String, Array<String>],
    required: true,
  },
  selectedOption: {
    type: String,
    required: false,
    default: "",
  },
});

const selected = computed(() => {
  return props.selectedOption === props.name;
});

const isImpersonated = computed(() => {
  return user?.is_impersonated;
});

const userFirstInitial = computed(() => {
  return user?.name[0];
});

const toolTipContent = computed(() => {
  const replacements = [
    "<span class='text-success-150'>",
    "</span><br>",
    "<span class='text-success-150'>",
    "</span>",
  ];

  const translation = t("sidebar.impersonating", {
    user: user?.name,
    org: org?.attributes.name,
  });

  return replaceAsterisks(translation, replacements);
});
</script>

<style scoped>
.sidebar-option__wrapper {
  @apply flex flex-col w-18 h-18 text-center justify-center cursor-pointer !py-0 text-white relative;
}
.sidebar-icon {
  @apply flex justify-center items-center my-3 min-h-[28px];
}
.isActive {
  @apply bg-alpha-b15 text-green-logo;
}

.sidebar-option__tip {
  @apply w-1 h-7.5 bg-green-logo absolute rounded-r;
}

.sidebar-tooltip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 6px;
  height: 28px;
  min-width: 71px;
  gap: 6px;

  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
}
</style>
